import { NgClass, NgOptimizedImage } from '@angular/common';
import { Component, Inject, Input, SimpleChanges, afterNextRender, DOCUMENT } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AthleticLiveElasticApiService } from '@athleticnet/athletic-live-api';
import { CommonEvent, IndividualResultsList } from '@athleticnet/athletic-live-api-models';
import { IndividualResult } from '@athleticnet/athletic-live-api-models/lib/models/shared/individual-result.interface';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  NgbDropdown,
  NgbDropdownButtonItem,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
} from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom } from 'rxjs';
import { XcTeamSummaryModalDataModel } from 'src/app/shared/models/xc-team-summary-data.model';
import { ResultsGridRowComponent } from 'src/app/shared/results-grid-main/results-grid-row.component';
import { environment } from '../../../environments/environment';
import { EventUpdatedAtComponent } from '../../event/event-updated-at/event-updated-at.component';
import { AthleteModalDataModel } from '../../shared/models/athlete-modal-data.model';
import { ModalService } from '../../shared/services/modal.service';
import { SeoService } from 'src/app/shared/services/seo.service';

@Component({
  selector: 'app-results',
  templateUrl: './results.component.html',
  styleUrls: ['./results.component.scss'],
  imports: [
    NgClass,
    FontAwesomeModule,
    ResultsGridRowComponent,
    RouterLink,
    NgbDropdown,
    NgbDropdownButtonItem,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    EventUpdatedAtComponent,
    NgOptimizedImage,
  ],
})
export class ResultsComponent {
  @Input() event!: CommonEvent;
  @Input() round!: string;
  @Input() limit!: number; // Only used by the results-tables component, IF USED ELSEWHERE FILTERING CODE MIGHT NEED CHANGING
  @Input() heat?: number;

  currentAgeGroup: { ageGroup: string; id: number } | null = null;
  currentPage: number = 0;
  multiAgeGroups: { ageGroup: string; id: number }[] = [];
  pageIndResultLists!: IndividualResultsList[];
  pageNames: string[] = [];
  scrollToTop: boolean = false;
  showThumbnail: boolean = false;
  heatNumbers: number[] = [];

  originalRawResults?: IndividualResultsList | null = null;
  typeWord = 'Heat';
  loadedEvent: boolean = false;
  loadedResults: boolean = false;

  constructor(
    private elasticApiService: AthleticLiveElasticApiService,
    private modalService: ModalService,
    private route: ActivatedRoute,
    private seoService: SeoService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    afterNextRender(() => {
      if (this.scrollToTop) {
        this.scrollToTop = false;
        const body = this.document.body;
        if (body && 'scrollIntoView' in body) {
          body.scrollIntoView({ behavior: 'instant', block: 'start', inline: 'center' });
        }
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    let refreshed = false;

    if (!refreshed && changes['event'] && changes['event'].currentValue !== changes['event'].previousValue) {
      // Live track in very rare cases can have no primary event group
      const eventGroup = this.event ? (this.event.primaryEventGroup ? this.event.primaryEventGroup : '') : '';
      this.typeWord = eventGroup.toLowerCase() === 'field' ? 'Flight' : 'Heat'; // TODO: replace with the new function
      this.loadedEvent = true;

      // Should only reload if the event results have changed
      if (
        changes['event'].isFirstChange() ||
        (changes['event'].previousValue &&
          changes['event'].currentValue &&
          (changes['event'].previousValue['updatedAt'] !== changes['event'].currentValue['updatedAt'] ||
            changes['event'].previousValue['eventNumber'] !== changes['event'].currentValue['eventNumber']))
      ) {
        await this.refreshResults(this.currentAgeGroup, !changes['event'].isFirstChange());
        refreshed = true;
      }
    }

    if ((!refreshed && changes['round']) || changes['limit'] || changes['heat']) {
      await this.refreshResults(this.currentAgeGroup);
    }
  }

  async refreshResults(multiAgeGroup: { ageGroup: string; id: number } | null = null, silentRefresh: boolean = false) {
    if (!silentRefresh) {
      this.loadedResults = false;
    }
    const eventId = this.getEventID(multiAgeGroup);

    this.originalRawResults = await firstValueFrom(this.elasticApiService.getIndividualResultsList(eventId));

    await this.processResults(this.heat);
    if (!this.loadedResults) {
      const queryParams = this.route.snapshot.queryParams;
      if (queryParams && queryParams['openAthlete'] && queryParams['openVideo']) {
        this.originalRawResults.individualResults.forEach((result) => {
          if (result.athlete.compNum.toString() === queryParams['openAthlete']) {
            this.openModal(result, this.event, queryParams['openVideo']);
          }
        });
      }
    }
    this.loadedResults = true;
    this.seoService.markAsLoaded();
  }

  async processResults(newHeatNumber?: number) {
    let rawResults = Object.assign({}, this.originalRawResults);

    if (rawResults && rawResults.individualResults) {
      // Always reset the heat numbers when the results are proccessed
      this.heatNumbers = Array.from<number>(
        rawResults.individualResults.reduce((acc, result) => acc.add(result.heatNumber), new Set<number>()),
      )
        .filter((heat) => !!heat)
        .sort((a, b) => {
          return a - b;
        });

      // Perform filtering
      // Priority
      // Heat > Limit

      // Filter by heat and assign the new heat number
      if (newHeatNumber && this.heatNumbers.includes(newHeatNumber)) {
        rawResults.individualResults = rawResults.individualResults.filter(
          (result) => result.heatNumber === newHeatNumber,
        );
        this.heat = newHeatNumber;
      } else {
        this.heat = undefined;
      }

      // Limit to the first X results
      if (this.limit && rawResults.individualResults.length > this.limit) {
        rawResults.individualResults = rawResults.individualResults.slice(0, this.limit);
      }

      const results = rawResults.individualResults ?? [];

      // Check if any results have a thubmnail
      this.showThumbnail = results.some((result) => !!result.imageThumbnail);

      this.paginateResults(results, rawResults);
    } else {
      this.pageIndResultLists = [];
      this.pageNames = [];
      this.heatNumbers = [];
    }
  }

  getEventID(multiAgeGroup: { ageGroup: string; id: number } | null = null): number {
    let eventId = this.event.id;

    if (this.round === 'prelims' && this.event.roundPrelimAliveId) {
      eventId = this.event.roundPrelimAliveId;
    } else if (this.round === 'qtrs' && this.event.roundQuarterAliveId) {
      eventId = this.event.roundQuarterAliveId;
    } else if (this.round === 'semis' && this.event.roundSemiAliveId) {
      eventId = this.event.roundSemiAliveId;
    } else if (this.round === 'finals' && this.event.roundFinalAliveId) {
      eventId = this.event.roundFinalAliveId;
    } else {
      const multiAgeGroups: { ageGroup: string; id: number }[] = [];
      if (this.event.multiAgeGroups) {
        multiAgeGroups.push({
          ageGroup: 'All ' + (this.event.multiAgeGroupTitle ?? 'Age') + 's',
          id: this.event.id,
        });
        Object.keys(this.event.multiAgeGroups).forEach((ageGroup) => {
          if (this.event.multiAgeGroups && this.event.multiAgeGroups[ageGroup]) {
            multiAgeGroups.push({
              ageGroup,
              id: this.event.multiAgeGroups[ageGroup],
            });
          }
        });
      }
      this.multiAgeGroups = multiAgeGroups;
      if (multiAgeGroup) {
        this.currentAgeGroup = multiAgeGroup;
      } else {
        this.currentAgeGroup = null;
      }

      if (this.currentAgeGroup) {
        eventId = this.currentAgeGroup.id;
      } else if (this.multiAgeGroups.length > 0 && !this.currentAgeGroup) {
        this.currentAgeGroup = this.multiAgeGroups[0];
        eventId = this.currentAgeGroup.id;
      }
    }

    return eventId;
  }

  paginateResults(results: IndividualResult[], rawResults: IndividualResultsList) {
    this.pageIndResultLists = [];
    this.pageNames = [];

    this.currentPage = 0;

    if (results.length > environment.paginationQuantity) {
      const pages = Math.ceil(results.length / environment.paginationQuantity);
      for (let i = 0, len = pages; i < len; i++) {
        const start = i * environment.paginationQuantity;
        let end = (i + 1) * environment.paginationQuantity - 1;
        if (end >= results.length) {
          end = results.length - 1;
        } else if (i === len - 1 && end === environment.paginationQuantity - 1) {
          end = results.length - 1;
        }

        const pageInfo =
          'Page ' +
          (i + 1) +
          ' (' +
          results[start].place +
          ' to ' +
          results[end].place +
          ') - (' +
          results[start].mark +
          ' to ' +
          results[end].mark +
          ')';
        this.pageNames.push(pageInfo);

        const modifiedResults = { ...rawResults }; // Create a shallow copy
        modifiedResults.individualResults = results.slice(start, end + 1);
        this.pageIndResultLists.push(modifiedResults);
      }
    } else {
      this.pageIndResultLists[0] = rawResults;
    }
  }

  async openModal(result: IndividualResult, event: CommonEvent, videoId?: string) {
    if (event.isXc === true) {
      await this.modalService.openXcTeamSummaryModalData(
        new XcTeamSummaryModalDataModel(event.meetId, result.athlete.team.id, result.athlete.id, event.id),
        this.route,
      );
    } else {
      let currentAttemptIdx = undefined;
      let currentJumpIdx = undefined;
      if (result.horizontalSeries && result.horizontalSeries.length > 0) {
        if (videoId) {
          result.horizontalSeries.forEach((series, idx) => {
            if (series.fieldAppVideoModel && series.fieldAppVideoModel.id === videoId) {
              currentAttemptIdx = idx;
            }
          });
        }
      }
      if (result.verticalSeries && result.verticalSeries.length > 0) {
        result.verticalSeries.forEach((series, attemptIdx) => {
          if (series.fieldAppVideoModels) {
            series.fieldAppVideoModels.forEach((video, jumpIdx) => {
              if (video && video.id === videoId) {
                currentAttemptIdx = attemptIdx;
                currentJumpIdx = jumpIdx;
              }
            });
          }
        });
      }

      await this.modalService.openAthleteModal(
        new AthleteModalDataModel(result.athlete.id, result, currentAttemptIdx, currentJumpIdx),
        this.route,
      );
    }
  }

  async changePage(newPage: number) {
    this.currentPage = newPage;
    this.scrollToTop = true;
  }

  protected readonly environment = environment;
}
